import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AppView, FormValue, UserStore, ViewStore } from '@coach-bot/data-access/shared';
import { ENVIRONMENT_TOKEN, Environment } from '@environment';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { ExtensionCommunicationService } from '@shared/extension';
import { NotificationService } from '@shared/ui';
import { BehaviorSubject, Observable, combineLatest, debounceTime, map, mergeMap, takeUntil } from 'rxjs';
import { FORM_VALUE_CHANGES_DEBOUNCE_TIME_MS } from '../../constants';
import { AuthState } from '../../states';
import { AbstractAuthComponent } from '../abstract/abstract-auth.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'fu-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['../../styles/auth.scss', 'sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignInComponent extends AbstractAuthComponent implements OnInit {
  readonly extensionNameFriendly$: BehaviorSubject<string> = this.viewStore.extensionNameFriendly$;
  readonly isSignedIn$: BehaviorSubject<boolean> = this.userStore.isSignedIn$;

  readonly actionDisabled$: Observable<boolean>;
  readonly description$: Observable<string> = this.extensionNameFriendly$.pipe(mergeMap((name: string) => {
    return this.translateService.stream('auth.sign_in.labels.description', { name });
  }));

  readonly isFormValid$ = new BehaviorSubject<boolean>(false);

  readonly icons = { faUpRightFromSquare };

  private readonly signUpUrl: string = `${this.environment.dashboardUrl}/auth/register`;

  constructor(
    protected override authState: AuthState,
    protected override notificationService: NotificationService,
    protected override viewStore: ViewStore,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
    private extensionCommunicationService: ExtensionCommunicationService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private userStore: UserStore,
  ) {
    super(authState, notificationService, viewStore);

    this.actionDisabled$ = combineLatest([this.isFormValid$, this.loading$]).pipe(
      takeUntil(this.destroyed$),
      map(([isFormValid, loading]: [boolean, boolean]) => !isFormValid || loading)
    );
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required],
    });

    if (this.userStore.signInFormValue$.value) {
      this.form.setValue(this.userStore.signInFormValue$.value);
    }

    this.updateIsFormValid();

    this.form.valueChanges
      .pipe(debounceTime(FORM_VALUE_CHANGES_DEBOUNCE_TIME_MS), takeUntil(this.destroyed$))
      .subscribe((value: FormValue) => {
        this.updateIsFormValid();
        this.userStore.setSignInFormValue(value);
      });
    
    this.actionDisabled$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
    });
  }

  signIn(): void {
    if (this.form.valid) {
      const { email, password } = this.form.value;
      this.authState.signIn(email, password);
    }
  }

  forgotPassword(event: MouseEvent): void {
    event.stopPropagation();
    this.viewStore.setView(AppView.ForgotPassword);
  }

  signUp(): void {
    this.extensionCommunicationService.navigateToUrl(this.signUpUrl);
  }

  private updateIsFormValid(): void {
    if (this.isFormValid$.value !== this.form.valid) {
      this.isFormValid$.next(this.form.valid);
    }
  }
}
